/**
 * Malay render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'ms')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const malayLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'blur' },
    change: { primary: 'change' },
    click: { primary: 'click' },
    dblclick: { primary: 'dblclick' },
    every: { primary: 'setiap' },
    focus: { primary: 'focus' },
    input: { primary: 'input' },
    keydown: { primary: 'keydown' },
    keypress: { primary: 'keypress' },
    keyup: { primary: 'keyup' },
    load: { primary: 'load' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'mouseout' },
    mouseover: { primary: 'mouseover' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'reset' },
    resize: { primary: 'resize' },
    scroll: { primary: 'scroll' },
    submit: { primary: 'submit' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: 'dan' },
    changes: { primary: 'berubah' },
    contains: { primary: 'mengandungi' },
    else: { primary: 'kalau_tidak' },
    empty: { primary: 'kosong' },
    end: { primary: 'tamat' },
    equals: { primary: 'sama' },
    exists: { primary: 'wujud' },
    false: { primary: 'salah' },
    has: { primary: 'ada' },
    have: { primary: 'ada' },
    if: { primary: 'jika' },
    includes: { primary: 'termasuk' },
    is: { primary: 'adalah' },
    live: { primary: 'langsung' },
    matches: { primary: 'sepadan' },
    not: { primary: 'bukan' },
    null: { primary: 'null' },
    or: { primary: 'atau' },
    otherwise: { primary: 'jika_tidak' },
    then: { primary: 'kemudian' },
    true: { primary: 'benar' },
    undefined: { primary: 'tidak_ditakrifkan' },
    when: { primary: 'apabila' },
    where: { primary: 'di_mana' },
  },
  temporal: {
    forever: { primary: 'selamanya' },
    h: { primary: 'j' },
    hour: { primary: 'jam' },
    hours: { primary: 'jam' },
    millisecond: { primary: 'milisaat' },
    milliseconds: { primary: 'milisaat' },
    min: { primary: 'min' },
    minute: { primary: 'minit' },
    minutes: { primary: 'minit' },
    ms: { primary: 'ms' },
    now: { primary: 'sekarang' },
    s: { primary: 's' },
    second: { primary: 'saat' },
    seconds: { primary: 'saat' },
    times: { primary: 'kali' },
  },
  values: {
    body: { primary: 'badan' },
    detail: { primary: 'perincian' },
    document: { primary: 'dokumen' },
    element: { primary: 'elemen' },
    event: { primary: 'peristiwa' },
    false: { primary: 'salah' },
    it: { primary: 'ia' },
    its: { primary: 'nya' },
    me: { primary: 'saya' },
    my: { primary: 'saya_punya' },
    myself: { primary: 'diri_saya' },
    null: { primary: 'null' },
    result: { primary: 'hasil' },
    target: { primary: 'sasaran' },
    true: { primary: 'benar' },
    undefined: { primary: 'tidak_ditakrifkan' },
    value: { primary: 'nilai' },
    window: { primary: 'tetingkap' },
    you: { primary: 'kamu' },
    your: { primary: 'kamu_punya' },
    yourself: { primary: 'diri_kamu' },
  },
  attributes: {
    attribute: { primary: 'atribut' },
    attributes: { primary: 'atribut_atribut' },
    checked: { primary: 'ditanda' },
    class: { primary: 'kelas' },
    classes: { primary: 'kelas_kelas' },
    disabled: { primary: 'dilumpuhkan' },
    html: { primary: 'html' },
    id: { primary: 'id' },
    properties: { primary: 'sifat_sifat' },
    property: { primary: 'sifat' },
    style: { primary: 'gaya' },
    styles: { primary: 'gaya_gaya' },
    text: { primary: 'teks' },
    value: { primary: 'nilai' },
  },
  expressions: {
    up: { primary: 'atas' },
    down: { primary: 'bawah' },
    left: { primary: 'kiri' },
    right: { primary: 'kanan' },
    at: { primary: 'di' },
    back: { primary: 'undur' },
    characters: { primary: 'aksara' },
    children: { primary: 'anak_anak' },
    closest: { primary: 'terdekat' },
    elsewhere: { primary: 'luar' },
    empty: { primary: 'kosong' },
    'ends with': { primary: 'berakhir_dengan' },
    exclusive: { primary: 'eksklusif' },
    first: { primary: 'pertama' },
    'ignoring case': { primary: 'abaikan_huruf_besar_kecil' },
    inclusive: { primary: 'inklusif' },
    index: { primary: 'indeks' },
    'joined by': { primary: 'digabung_oleh' },
    last: { primary: 'terakhir' },
    length: { primary: 'panjang' },
    'mapped to': { primary: 'dipetakan_ke' },
    next: { primary: 'seterusnya' },
    no: { primary: 'tiada' },
    parent: { primary: 'induk' },
    prev: { primary: 'sebelum' },
    previous: { primary: 'sebelumnya' },
    random: { primary: 'rawak' },
    some: { primary: 'beberapa' },
    'sorted by': { primary: 'disusun_mengikut' },
    'split by': { primary: 'dipecah_oleh' },
    start: { primary: 'memulakan' },
    catch: { primary: 'tangkap' },
    finally: { primary: 'akhirnya' },
    def: { primary: 'fungsi' },
    'starts with': { primary: 'bermula_dengan' },
    within: { primary: 'dalam' },
  },
};

registerLexicon('ms', malayLexicon);
